import {assertAndContinue} from 'Asserts';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';

interface CheckStructuredTableSingleInputsValidOptions {
  /** assertAndContinue when a Single Input is broken. */
  softAssertAndWarn?: boolean;
}

/**
 * Checks a StructuredTable for its Single Inputs and logs a warning if there is anything wrong with
 * them. In particular, it checks that all Single Inputs are at the front of the table, contiguous, and
 * their merges span all rows.
 *
 * Returns true if all Single Inputs are valid, false otherwise.
 */
export default function checkStructuredTableSingleInputsValid(
  structuredTable: UnknownStructuredTable,
  {softAssertAndWarn = false}: CheckStructuredTableSingleInputsValidOptions = {}
): boolean {
  let allSingleInputsOkay = true;
  /** a secondary tracking index to make sure that all the Single Inputs are grouped together at the front of
   * the table */
  let singleInputBoundaryIndex = -1;
  for (const [columnIndex, column] of structuredTable.columns.entries()) {
    if (!column.isSingleInput) {
      continue;
    }

    const isSingleInputContiguous = columnIndex === singleInputBoundaryIndex + 1;
    if (softAssertAndWarn) {
      assertAndContinue(
        isSingleInputContiguous,
        'Single Inputs must be contiguous and at the front of the table'
      );
    }
    singleInputBoundaryIndex = columnIndex;

    const singleInputCell = structuredTable.rows[0].cells[columnIndex];
    const doesSingleInputCellSpanAllRows = singleInputCell.mergeRowSpan === structuredTable.rows.length;
    if (softAssertAndWarn) {
      assertAndContinue(doesSingleInputCellSpanAllRows, 'Single Input must span all rows');
    }
    const doesSingleInputCellSpanOneColumn = singleInputCell.mergeColSpan === 1;
    if (softAssertAndWarn) {
      assertAndContinue(doesSingleInputCellSpanOneColumn, 'Single Input must not span multiple columns');
    }

    allSingleInputsOkay =
      allSingleInputsOkay &&
      isSingleInputContiguous &&
      doesSingleInputCellSpanAllRows &&
      doesSingleInputCellSpanOneColumn;
  }
  return allSingleInputsOkay;
}
